<script lang="ts">
  import { magneticHue, snapPercentEndpoint } from './core/colour';

  export let target: 'fill' | 'stroke';
  export let colour: string;
  export let enabled: boolean;
  export let h: number;
  export let s: number;
  export let l: number;
  export let alpha: number;
  export let colours: string[] = [];
  export let hexDraft: string | null = null;
  export let hexEditing = false;
  export let eyedropperActive = false;
  export let onToggle: () => void;
  export let onSwatch: (colour: string) => void;
  export let onBegin: () => void;
  export let onChange: (part: 'h' | 's' | 'l' | 'a', value: number) => void;
  export let onCommit: (label: string) => void;
  export let onCancel: () => void;
  export let onHexFocus: () => void;
  export let onHexInput: (value: string) => void;
  export let onHexCommit: () => void;
  export let onEyedropper: () => void;

  const sliderValue = (event: Event) => Number((event.currentTarget as HTMLInputElement).value);
  let dragging = false;
  let liveH = h, liveS = s, liveL = l, liveAlpha = alpha;
  $: if (!dragging) { liveH = h; liveS = s; liveL = l; liveAlpha = alpha; }
  function begin() { dragging = true; liveH = h; liveS = s; liveL = l; liveAlpha = alpha; onBegin(); }
  function preview(part: 'h' | 's' | 'l' | 'a', value: number) {
    const next = part === 'h' ? magneticHue(value) : snapPercentEndpoint(value);
    if (part === 'h') liveH = next; else if (part === 's') liveS = next / 100; else if (part === 'l') liveL = next / 100; else liveAlpha = next / 100;
    onChange(part, next);
  }
  function commit(label:string) { dragging = false; onCommit(label); }
  function cancel() { dragging = false; onCancel(); }
</script>

<div class="colour-picker" aria-label={`${target} colour picker`}>
  <div class="colour-picker-head">
    <button class:off={!enabled} class="colour-well" style={`--colour:${colour}`} on:click={onToggle} aria-label={`${enabled ? 'Disable' : 'Enable'} ${target}`}></button>
    <strong>{target.toUpperCase()}</strong>
    <div class="document-swatches" aria-label="Document colours">{#each colours as swatch}<button style={`--swatch:${swatch}`} aria-label={`Use ${swatch}`} on:click={() => onSwatch(swatch)}></button>{/each}</div>
    <button class:active={eyedropperActive} class="eyedropper" on:click={onEyedropper} aria-label="Eyedropper"><img src="/ui/toolbar/eyedropper.svg" alt="" /></button>
    <input class="hex-input" aria-label={`${target} hex colour`} value={hexEditing ? (hexDraft ?? '') : colour.toUpperCase()} on:focus={onHexFocus} on:input={(event) => onHexInput(event.currentTarget.value)} on:blur={onHexCommit} on:keydown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }} />
  </div>
  <label class="colour-row hue"><span>H</span><input aria-label="Hue" type="range" min="0" max="360" step="1" value={liveH} on:pointerdown={begin} on:pointercancel={cancel} on:input={(event) => preview('h', sliderValue(event))} on:change={() => commit(`${target} hue`)} /><output>{Math.round(liveH)}°</output></label>
  <label class="colour-row saturation" style={`--hue:${liveH}`}><span>S</span><input aria-label="Saturation" type="range" min="0" max="100" step="1" value={liveS * 100} on:pointerdown={begin} on:pointercancel={cancel} on:input={(event) => preview('s', sliderValue(event))} on:change={() => commit(`${target} saturation`)} /><output>{Math.round(liveS * 100)}%</output></label>
  <label class="colour-row lightness" style={`--hue:${liveH};--sat:${liveS * 100}%`}><span>L</span><input aria-label="Lightness" type="range" min="0" max="100" step="1" value={liveL * 100} on:pointerdown={begin} on:pointercancel={cancel} on:input={(event) => preview('l', sliderValue(event))} on:change={() => commit(`${target} lightness`)} /><output>{Math.round(liveL * 100)}%</output></label>
  <label class="colour-row alpha" style={`--colour:${colour}`}><span>A</span><input aria-label={`${target} opacity`} type="range" min="0" max="100" step="1" value={liveAlpha * 100} on:pointerdown={begin} on:pointercancel={cancel} on:input={(event) => preview('a', sliderValue(event))} on:change={() => commit(`${target} opacity`)} /><output>{Math.round(liveAlpha * 100)}%</output></label>
</div>
